import { forwardRef, ReactNode, useCallback, useEffect, useMemo, useRef } from "react";

import { colorToAlpha, filterTruthy } from "@bridgesplit/utils";
import {
    Box,
    ButtonBaseProps,
    CircularProgress,
    Button as MuiButton,
    ButtonBase as MuiButtonBase,
    ButtonProps as MuiButtonProps,
    IconButton as MuiIconButton,
    IconButtonProps as MuiIconButtonProps,
    ToggleButton as MuiToggleButton,
    ToggleButtonProps as MuiToggleButtonProps,
    ToggleButtonGroup as MuiToggleButtonGroup,
    styled
} from "@mui/material";
import { ChevronRightRounded, ExpandLessRounded, ExpandMoreRounded, SwapHoriz } from "@mui/icons-material";
import { DispatchType } from "@bridgesplit/react";

import {
    BORDER_RADIUS,
    BUTTON_HEIGHT,
    BUTTON_HEIGHT_SM,
    FONT_WEIGHTS,
    INPUT_HEIGHT,
    SPACING,
    SPACING_PX,
    useAppPalette,
    useTextColorMap
} from "../theme";
import { SxType } from "../types";
import { Icon, IconType } from "./icons";
import { ExternalLink, Link, Span, Text, TextColor, TextProps, TextVariant, TooltipText } from "./typography";
import { Column, Row } from "./containers";
import { TrackingInfo, useUiTrack } from "../tracking";
import { Tooltip } from "./misc";
import { useScrollNavigation } from "../util/hooks";
import { StatCardContent, StatCardProps } from "./info";

export interface ButtonProps extends Omit<MuiButtonProps, "variant"> {
    loading?: boolean;
    color?: "primary" | "secondary" | "error" | "info" | "success";
    variant?: "text" | "default" | "outlined" | "contained";
    width?: string;
    textProps?: Omit<TextProps, "children">;
    height?: number;
    tracking?: TrackingInfo;
    noHover?: boolean;
    dataTestId?: string;
}

const circularProgressSx = (loading?: boolean) => ({
    transition: "opacity 0.2s",
    opacity: loading ? 1 : 0,
    position: "absolute",
    left: 0,
    right: 0,
    margin: "auto"
});

export function Button({
    children,
    variant = "default",
    loading,
    width,
    height: heightProps,
    color = "primary",
    sx,
    disabled: propsDisabled,
    textProps,
    onClick,
    tracking,
    noHover,
    dataTestId,
    ...props
}: ButtonProps) {
    const height = heightProps ?? (props.size === "small" ? BUTTON_HEIGHT_SM : BUTTON_HEIGHT);
    const { sx: textSxProps, ...textNonSxProps } = textProps || {};

    const { background: backgroundColor, isDarkMode, textDisabled, inputBorder } = useAppPalette();
    const getColor = useTextColorMap();
    const baseColor = getColor(color);
    const graphicColor = getColor(color, true);

    const trackEvent = useUiTrack();

    const textSx: SxType =
        variant === "text"
            ? {
                  height: heightProps ?? "min-content",
                  minHeight: heightProps ?? "min-content",
                  py: 0,
                  px: 1.5,
                  mx: -1.5,
                  fontWeight: FONT_WEIGHTS.bold
              }
            : {};

    const { background, text, outline, hoverAlpha } = useMemo(() => {
        switch (variant) {
            case "contained":
                return {
                    background: graphicColor,
                    text: color === "secondary" ? "white" : backgroundColor,
                    outline: undefined,
                    hoverAlpha: 0.7
                };
            case "default":
                return {
                    background: colorToAlpha(baseColor, isDarkMode ? 0.1 : 0.05),
                    text: baseColor,
                    outline: undefined,
                    hoverAlpha: isDarkMode ? 0.2 : 0.1
                };
            case "text":
                return {
                    background: "none",
                    text: baseColor,
                    outline: undefined,
                    hoverAlpha: noHover ? 0 : isDarkMode ? 0.1 : 0.05
                };
            case "outlined":
                return {
                    background: undefined,
                    text: baseColor,
                    outline: inputBorder,
                    hoverAlpha: isDarkMode ? 0.1 : 0.05
                };
            default:
                return { background: undefined, text: undefined, outline: undefined, hoverAlpha: undefined };
        }
    }, [backgroundColor, baseColor, graphicColor, color, inputBorder, isDarkMode, variant, noHover]);
    const disabled = propsDisabled || loading;

    return (
        <MuiButton
            data-testid={dataTestId}
            color={color}
            disableElevation
            variant={variant === "default" ? "text" : variant}
            disabled={disabled}
            onClick={(e) => {
                if (tracking) trackEvent(tracking);
                if (onClick) onClick(e);
            }}
            sx={{
                position: "relative",
                ":hover": {
                    backgroundColor: colorToAlpha(baseColor, hoverAlpha),
                    border: outline ? `1px solid ${outline}` : undefined,
                    opacity: noHover ? 0.6 : 1
                },
                cursor: disabled ? "not-allowed" : undefined,
                px: 1.5,
                backgroundColor: background,
                opacity: disabled && variant !== "contained" ? 0.5 : 1,
                border: outline ? `1px solid ${outline}` : undefined,
                minHeight: `${height}px`,
                maxHeight: `${height}px`,
                width: width ? width : undefined,
                maxWidth: width ? width : undefined,
                minWidth: width ? width : undefined,
                overflow: "hidden",
                borderRadius: BORDER_RADIUS,
                transition: "all 0.2s ease",
                ...textSx,
                ...sx
            }}
            {...props}
        >
            <CircularProgress sx={circularProgressSx(loading)} color="inherit" size="1rem" />
            <Text
                {...textNonSxProps}
                sx={{
                    color: disabled ? textDisabled : text,
                    textTransform: "initial",
                    opacity: loading ? 0.1 : 1,
                    transition: "opacity 0.2s",
                    fontWeight: FONT_WEIGHTS.bold,
                    ...textSxProps
                }}
            >
                {children}
            </Text>
        </MuiButton>
    );
}

export function TextExternalLink({
    href,
    children,
    sx,
    isPrimary,
    underlineLink,
    tracking
}: {
    href: string;
    children: ReactNode;
    sx?: SxType;
    isPrimary?: boolean;
    underlineLink?: boolean;
    tracking?: TrackingInfo;
}) {
    const track = useUiTrack();

    return (
        <ExternalLink
            href={href}
            onClick={tracking ? () => track(tracking) : undefined}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
                color: ({ palette }) => (isPrimary ? palette.secondary.main : palette.primary.main),
                opacity: 1,
                transition: "opacity 0.2s",
                "&:hover": {
                    opacity: 0.7
                },
                ...sx
            }}
            underline={underlineLink}
        >
            <Span>{children}</Span>
        </ExternalLink>
    );
}

export function ConditionalLink({
    to,
    children,
    sx,
    disabled
}: {
    to?: string;
    disabled?: boolean;
    children: ReactNode;
    sx?: SxType;
}) {
    if (!to || disabled) {
        return <Box sx={{ cursor: disabled ? "not-allowed" : undefined, ...sx }}>{children}</Box>;
    }
    return (
        <Link sx={{ cursor: to ? "pointer" : "default", ...sx }} to={to || "#"}>
            {children}
        </Link>
    );
}

export interface IconButtonProps extends Omit<MuiIconButtonProps, "color"> {
    icon?: IconType;
    jsxIcon?: ReactNode;
    background?: "always" | "hover" | "never";
    textVariant?: TextVariant;
    color?: "primary" | "success" | "error" | "info" | "secondary";
    textColor?: TextColor;
    border?: boolean;
    loading?: boolean;
}

export function IconButton({
    children,
    jsxIcon,
    background = "hover",
    textVariant = "h3",
    color = "primary",
    textColor,
    icon,
    sx,
    border = true,
    loading,
    ...props
}: IconButtonProps) {
    const baseColor = useTextColorMap()(color);
    return (
        <MuiIconButton
            disableRipple={background === "never"}
            color={color}
            disabled={loading}
            sx={{
                width: border ? undefined : "max-content",
                p: 0.5,
                border: border ? `1px solid ${colorToAlpha(baseColor, 0.1)}` : undefined,
                background: background === "always" ? colorToAlpha(baseColor, 0.05) : undefined,
                ":hover": { background: background !== "never" ? colorToAlpha(baseColor, 0.1) : undefined },
                ...sx
            }}
            {...props}
        >
            {loading ? (
                <CircularProgress sx={circularProgressSx(loading)} color="inherit" size="1rem" />
            ) : (
                <Text variant={textVariant} color={textColor ?? color}>
                    {!!icon && <Icon type={icon as IconType} />}
                    {jsxIcon}
                </Text>
            )}

            {children}
        </MuiIconButton>
    );
}

const ToggleButtonGroup = styled(MuiToggleButtonGroup)(() => ({}));

const ToggleButton = styled(MuiToggleButton)(() => ({
    padding: SPACING_PX,
    gap: `${SPACING / 2}px`,
    fontWeight: FONT_WEIGHTS.normal,
    textTransform: "none",
    svg: {
        fontSize: "inherit"
    },
    borderRadius: BORDER_RADIUS
}));

const PillTabSwitcherContainer = styled("div")(({ theme }) => [
    theme.applyStyles("dark", {
        backgroundColor: colorToAlpha(theme.palette.primary.main, 0.08)
    }),
    {
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: 4,
        borderRadius: 9999,
        border: `1px solid ${theme.palette.divider}`,
        background: colorToAlpha(theme.palette.text.primary, 0.04)
    }
]);

const PillTabButton = styled("button")<{ $selected: boolean }>(({ theme, $selected }) => ({
    padding: "4px 14px",
    borderRadius: 9999,
    border: "none",
    outline: "none",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: FONT_WEIGHTS.semibold,
    fontFamily: "inherit",
    transition: "color 0.2s, background 0.2s",
    ...($selected
        ? {
              background: theme.palette.text.primary,
              color: theme.palette.background.paper,
              boxShadow: "0 1px 2px rgba(0,0,0,0.05)"
          }
        : {
              background: "transparent",
              color: theme.palette.text.secondary,
              "&:hover": {
                  color: theme.palette.text.primary
              }
          }),
    "&:disabled": {
        opacity: 0.5,
        cursor: "not-allowed"
    }
}));

export type PillTabOption<T> = {
    label?: ReactNode;
    value: NonNullable<T>;
    disabled?: boolean;
};

/** Pill tab switcher – selected pill sits inside a rounded container. */
export function PillTabSwitcher<T extends string | number>({
    value,
    onChange,
    options,
    sx
}: {
    value: T | undefined;
    onChange: (val: T) => void;
    options: PillTabOption<T>[];
    sx?: SxType;
}) {
    return (
        <PillTabSwitcherContainer sx={sx}>
            {options.map((o, i) => (
                <PillTabButton
                    key={o.value.toString() + i}
                    type="button"
                    $selected={value === o.value}
                    disabled={o.disabled}
                    onClick={() => onChange(o.value)}
                >
                    {o.label ?? o.value}
                </PillTabButton>
            ))}
        </PillTabSwitcherContainer>
    );
}

export type ToggleOption<T> = {
    label?: ReactNode;
    value: NonNullable<T>;
    disabled?: boolean;
    toggleProps?: Partial<MuiToggleButtonProps>;
    tooltipText?: string;
    icon?: IconType | JSX.Element; //Used for toggle box buttons
};

export function ToggleButtons<T extends string | number>({
    value,
    setValue,
    options,
    containerSx,
    onClick,
    allowNull,
    size
}: ToggleButtonsProps<T>) {
    const buttonSxProps =
        size === "xsmall"
            ? {
                  fontSize: "14px",
                  minHeight: SPACING * 3 + "px",
                  height: SPACING * 3 + "px",
                  maxHeight: SPACING * 3 + "px"
              }
            : size === "small"
            ? {
                  fontSize: "14px",
                  minHeight: BUTTON_HEIGHT_SM + "px",
                  height: BUTTON_HEIGHT_SM + "px",
                  maxHeight: BUTTON_HEIGHT_SM + "px"
              }
            : {
                  fontSize: "16px",
                  minHeight: INPUT_HEIGHT + "px",
                  height: INPUT_HEIGHT + "px",
                  maxHeight: INPUT_HEIGHT + "px"
              };
    return (
        <ToggleButtonGroup
            onClick={onClick}
            sx={containerSx}
            fullWidth
            value={value}
            onChange={(_, val) => {
                if (val === null && !allowNull) return;
                setValue(val);
            }}
            exclusive
        >
            {options.map((o, i) => (
                <ToggleButton
                    disabled={o.disabled}
                    key={o.value.toString() + i}
                    fullWidth
                    value={o.value}
                    translate="no"
                    sx={buttonSxProps}
                    {...o.toggleProps}
                >
                    {o.label ?? o.value}
                </ToggleButton>
            ))}
        </ToggleButtonGroup>
    );
}

export type ToggleButtonsProps<T> = {
    value: T | undefined;
    setValue: (val: T) => void;
    options: ToggleOption<T>[];
    containerSx?: SxType;
    onClick?: () => void;
    size?: "medium" | "small" | "xsmall";
    variant?: "contained" | "default";
    showBorder?: boolean;
    allowNull?: boolean;
    ref?: React.RefObject<HTMLDivElement>;
};

export function ToggleSpacedButtons<T extends string | number>({
    value,
    setValue,
    options,
    containerSx,
    onClick,
    buttonSx,
    variant = "default",
    size = "medium",
    textSx,
    showBorder = false
}: ToggleButtonsProps<T> & { buttonSx?: SxType; tooltipText?: string; textSx?: SxType }) {
    const { textSecondary, divider, cardBackground, secondary } = useAppPalette();
    const isContained = variant === "contained";

    return (
        <Row spacing={1} onClick={onClick} sx={containerSx}>
            {options.map((o, i) => {
                const isSelected = value === o.value;
                const button = (
                    <Button
                        disableRipple
                        variant={(() => (isSelected ? variant : "outlined"))()}
                        onClick={() => setValue(o.value)}
                        disabled={o.disabled}
                        key={o.value.toString() + i}
                        width="max-content"
                        color={isContained && isSelected ? "secondary" : "primary"}
                        sx={{
                            borderRadius: "100px",
                            px: 1.5,
                            background: (() => {
                                if (isContained) {
                                    return isSelected ? secondary : cardBackground;
                                }
                                return undefined;
                            })(),
                            pointerEvents: isSelected ? "none" : undefined,
                            border: `${showBorder ? "1px solid " + divider : "none"} !important`,
                            minHeight: "fit-content",
                            ...buttonSx
                        }}
                        value={o.value}
                        height={size === "small" ? 35 : 40}
                        textProps={{
                            sx: {
                                fontWeight: "semibold",
                                color:
                                    variant === "default" && !isSelected
                                        ? textSecondary
                                        : isContained && isSelected
                                        ? "white"
                                        : undefined,
                                ...(textSx ?? {})
                            },
                            variant: size === "small" ? "h4" : "h4"
                        }}
                    >
                        {o.label ?? o.value}
                    </Button>
                );

                return o?.tooltipText ? (
                    <Tooltip key={o.value.toString() + i} title={o.tooltipText}>
                        {button}
                    </Tooltip>
                ) : (
                    button
                );
            })}
        </Row>
    );
}

type ToggleStatButtonProps<T extends string | number> = {
    options: Partial<Record<T, StatCardProps>>;
} & Omit<ToggleButtonsProps<T>, "options">;

export function ToggleStatButtons<T extends string | number>({ options, ...props }: ToggleStatButtonProps<T>) {
    const ButtonContent = useCallback(
        (statProps: StatCardProps) => <StatCardContent variant="h3" captionVariant="body2" {...statProps} />,
        []
    );
    const tabOptions = (Object.keys(options) as T[])
        .map((key) => {
            if (!options[key] || options[key].hide) return null;
            return {
                label: <ButtonContent {...options[key]} />,
                value: key
            };
        })
        .filter(filterTruthy);

    return (
        <Box sx={{ width: "100%", minWidth: 0, maxWidth: "100%", overflow: "hidden" }}>
            <Box
                sx={{
                    display: "flex",
                    gap: `${SPACING}px`,
                    overflowX: "auto",
                    scrollbarWidth: "thin",
                    width: "100%"
                }}
            >
                <ToggleSpacedButtons
                    options={tabOptions}
                    containerSx={{
                        display: "contents"
                    }}
                    {...props}
                    buttonSx={{
                        borderRadius: BORDER_RADIUS,
                        minWidth: "140px",
                        px: 2,
                        py: 1,
                        maxWidth: "unset",
                        justifyContent: "flex-start",
                        pointerEvents: "auto",
                        overflow: "hidden",
                        flex: "0 0 auto"
                    }}
                />
            </Box>
        </Box>
    );
}

export const ToggleBoxButtons = forwardRef<HTMLDivElement, ToggleButtonsProps<string | number>>(
    ({ value, setValue, options, containerSx, onClick, size = "medium" }, ref) => {
        const { textSecondary, border, cardBackground, secondary, hoverOpacity } = useAppPalette();

        // Store refs for each option
        const optionRefs = useRef<Record<string | number, HTMLDivElement | null>>({});

        useEffect(() => {
            const selectedRef = optionRefs.current[value as string | number];
            if (selectedRef) {
                selectedRef.scrollIntoView({
                    behavior: "smooth",
                    inline: "center", // horizontally align
                    block: "nearest" // vertical alignment
                });
            }
        }, [value]);

        return (
            <Row spacing={1.5} onClick={onClick} sx={containerSx} ref={ref}>
                {options.map((o, i) => {
                    const isSelected = value === o.value;

                    return (
                        <Box
                            key={o.value.toString() + i}
                            ref={(el) => {
                                optionRefs.current[o.value] = el as HTMLDivElement;
                            }}
                            onClick={() => setValue(o.value)}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                borderRadius: BORDER_RADIUS,
                                border: border,
                                background: isSelected ? secondary : cardBackground,
                                cursor: "pointer",
                                opacity: o.disabled ? 0.5 : 1,
                                height: size === "small" ? 40 : 60,
                                minWidth: "156px",
                                p: 2,
                                "&:hover": {
                                    backgroundColor: isSelected
                                        ? secondary
                                        : colorToAlpha(cardBackground, hoverOpacity),
                                    transition: "background-color 0.2s ease"
                                }
                            }}
                        >
                            <Column spacing={1}>
                                {o.icon &&
                                    (typeof o.icon === "string" ? (
                                        <Icon
                                            type={o.icon}
                                            sx={{
                                                color: isSelected ? "white" : textSecondary
                                            }}
                                        />
                                    ) : (
                                        o.icon
                                    ))}
                                <Text
                                    variant={size === "small" ? "body2" : "h4"}
                                    sx={{
                                        color: isSelected ? "white" : textSecondary,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis"
                                    }}
                                >
                                    {o.label ?? o.value}
                                </Text>
                            </Column>
                        </Box>
                    );
                })}
            </Row>
        );
    }
);

export function ToggleTextButtons<T extends string | number>({
    value,
    setValue,
    options,
    containerSx,
    onClick
}: ToggleButtonsProps<T>) {
    return (
        <Row spacing={1} onClick={onClick} sx={containerSx}>
            {options.map((o, i) => (
                <Button
                    variant="text"
                    onClick={() => setValue(o.value)}
                    disabled={o.disabled}
                    key={o.value.toString() + i}
                    width="max-content"
                    sx={{
                        opacity: value === o.value ? 1 : 0.5,
                        "&:hover": {
                            backgroundColor: "transparent",
                            opacity: value === o.value ? 1 : 0.75
                        }
                    }}
                    value={o.value}
                    disableFocusRipple
                    disableTouchRipple
                >
                    {o.label ?? o.value}
                </Button>
            ))}
        </Row>
    );
}

export function ErrorWrapper({
    errorMessage,
    children,
    fullWidth,
    color,
    tooltipText
}: {
    errorMessage: string | undefined;
    children: ReactNode;
    fullWidth?: boolean;
    color?: TextColor;
    tooltipText?: string;
}) {
    const showError = !!errorMessage;
    const showRow = showError || tooltipText;

    return (
        <Column sx={{ width: fullWidth ? "100%" : undefined }}>
            {showRow && (
                <Row
                    sx={{
                        alignItems: "center",
                        mb: 0.5,
                        gap: 0.5
                    }}
                >
                    {showError && (
                        <Text
                            variant="body2"
                            color={color ?? "error"}
                            sx={{
                                transition: "opacity 0.3s"
                            }}
                        >
                            {errorMessage}
                        </Text>
                    )}
                    {tooltipText && <TooltipText helpText={tooltipText} />}
                </Row>
            )}
            {children}
        </Column>
    );
}

interface TextButtonProps extends Omit<ButtonProps, "variant" | "color" | "textProps"> {
    to?: string;
    underlineLink?: boolean;
    color?: TextColor;
    variant?: TextVariant;
    textProps?: Omit<TextProps, "children">;
}
export function TextButton({ variant, color = "body", to, underlineLink, width, ...props }: TextButtonProps) {
    const rawColor = useTextColorMap()(color);

    const button = (
        <Button
            disableRipple
            sx={{ p: 0, m: 0, ":hover": { background: "none", opacity: 0.6 }, verticalAlign: "baseline", ...props.sx }}
            textProps={{
                color,
                underlineLink: underlineLink ? "always" : "never",
                variant,
                isLink: true,
                sx: { wordBreak: "break-all", color: rawColor, ...props.textProps?.sx }
            }}
            width={width ?? "max-content"}
            variant="text"
            {...props}
        />
    );
    if (!to) return button;
    return <ConditionalLink to={to}>{button}</ConditionalLink>;
}

export function ButtonBase({
    sx,
    disableRipple = true,
    px = 1,
    py = 0.5,
    ...props
}: ButtonBaseProps & { px?: number; py?: number }) {
    const { hoverBackground } = useAppPalette();
    return (
        <MuiButtonBase
            disableRipple={disableRipple}
            sx={{
                ":hover": { background: hoverBackground },
                gap: 1,
                mx: -px,
                my: -py,
                px,
                py,
                borderRadius: BORDER_RADIUS,
                ...sx
            }}
            tabIndex={0}
            {...props}
        />
    );
}

type ToggleChevronProps = Omit<IconButtonProps, "jsxIcon" | "children" | "textColor"> & { expanded: boolean };
export function ToggleChevron({ expanded, ...props }: ToggleChevronProps) {
    return (
        <IconButton
            {...props}
            textColor="disabled"
            jsxIcon={expanded ? <ExpandLessRounded /> : <ExpandMoreRounded />}
            border={props.border ?? false}
            background="never"
        />
    );
}

export function HorizToggleChevron({ expanded, ...props }: ToggleChevronProps) {
    return (
        <IconButton
            {...props}
            textColor="disabled"
            jsxIcon={expanded ? <ExpandMoreRounded /> : <ChevronRightRounded />}
            border={props.border ?? false}
            background="never"
        />
    );
}

export function HorizontalScrollNavigationButtons({
    containerRef,
    scrollByAmount
}: {
    containerRef: React.RefObject<HTMLDivElement>;
    scrollByAmount?: number;
}) {
    const { atStart, atEnd, scrollLeft, scrollRight } = useScrollNavigation(containerRef, scrollByAmount);

    return (
        <Row spacing={1}>
            <IconButton
                icon="navigate-back"
                onClick={scrollLeft}
                border={false}
                textColor={atStart ? "disabled" : "primary"}
                background="never"
                disabled={atStart}
            />
            <IconButton
                icon="navigate-forward"
                onClick={scrollRight}
                border={false}
                textColor={atEnd ? "disabled" : "primary"}
                background="never"
                disabled={atEnd}
            />
        </Row>
    );
}

export function DialogToggleButton<T extends string | number>({
    value,
    setValue,
    options
}: {
    value: T;
    setValue: DispatchType<T>;
    options: ToggleOption<T>[];
}) {
    const currentOption = options.find((opt) => opt.value === value);
    const toggleToNext = () => {
        const otherOption = options.find((opt) => opt.value !== value);
        if (otherOption) setValue(otherOption.value);
    };

    return (
        <Button variant="outlined" onClick={toggleToNext} height={35}>
            <Row spacing={0.5} sx={{ alignItems: "center" }}>
                <Text variant="body1">{currentOption?.label ?? value}</Text>
                <SwapHoriz fontSize="small" />
            </Row>
        </Button>
    );
}
